import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { PersonStanding, Languages } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import AccessibilityPanel from "./AccessibilityPanel";

const ProfileSidebar = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false); // controls mobile drawer
  const { language, updateLanguage } = useContext(AuthContext);
  const openButtonRef = useRef(null);
  const closeButtonRef = useRef(null);
  const prevOpenRef = useRef(false);

  // Accessibility panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const mobileA11yRef = useRef(null);
  const desktopA11yRef = useRef(null);

  // Close panel on outside click
  useEffect(() => {
    const handleMouseDown = (e) => {
      const outsideMobile = !mobileA11yRef.current || !mobileA11yRef.current.contains(e.target);
      const outsideDesktop = !desktopA11yRef.current || !desktopA11yRef.current.contains(e.target);
      if (outsideMobile && outsideDesktop) setPanelOpen(false);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, []);

  // Close panel on Escape
  useEffect(() => {
    if (!panelOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setPanelOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [panelOpen]);

  // Move focus to close button when drawer opens; return to open button on close
  useEffect(() => {
    if (open && closeButtonRef.current) {
      closeButtonRef.current.focus();
    } else if (!open && prevOpenRef.current && openButtonRef.current) {
      openButtonRef.current.focus();
    }
    prevOpenRef.current = open;
  }, [open]);

  // Close drawer on Escape key
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  const toggleLanguage = () => {
    const newLang = language === "el" ? "en" : "el";
    updateLanguage(newLang);
  };

  return (
    <>
      {/*mobile button (small screens)
      <button
        id="sidebar-open-btn"
        ref={openButtonRef}
        onClick={() => setOpen(true)}
        aria-label={language === 'el' ? 'Άνοιγμα πλευρικής μπάρας' : 'Open sidebar'}
        aria-expanded={open}
        aria-controls="profile-sidebar"
        className="xl:hidden fixed top-24 left-2 z-50 bg-custom-header text-custom-letters text-3xl font-bold p-2 rounded-full shadow-lg focus:outline-none focus:ring-2 focus:ring-custom-letters">
        ➤
      </button>
      */}

      {/*mobile overlay*/}
      {open && (
        <div
          aria-hidden="true"
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 xl:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/*slideout sidebar (mobile) */}
      <div
        id="profile-sidebar"
        role="dialog"
        aria-modal="true"
        aria-label={language === 'el' ? 'Πλευρική μπάρα' : 'Sidebar'}
        className={`
          fixed top-0 left-0 h-full w-56 bg-custom-header z-50 xl:hidden transform transition-transform duration-300
          ${open ? "translate-x-0" : "-translate-x-full"}`}
        style={{ width: "60px" }}>

        {/* close button */}
        <button
          ref={closeButtonRef}
          onClick={() => setOpen(false)}
          aria-label={language === 'el' ? 'Κλείσιμο πλευρικής μπάρας' : 'Close sidebar'}
          className="absolute top-4 left-1/2 -translate-x-1/2 text-custom-letters text-3xl font-bold focus:outline-none focus:ring-2 focus:ring-custom-letters rounded">
          ✕
        </button>

        {/* Mobile-specific action buttons have been moved to the header for small screens. */}
      </div>



      {/*desktop sidebar*/}
      <div id="desktop-sidebar" className="hidden xl:block">
        {/* side bar background */}
        <div
          className="absolute top-0 left-0 h-full bg-custom-header"
          style={{ width: "50px" }}
        ></div>

        {/* icon */}
        <div className="absolute top-6 left-1 flex flex-col items-center z-20">
          {/* Accessibility Panel Toggle — desktop */}
          <div ref={desktopA11yRef} className="relative">
            <button
              onClick={() => setPanelOpen((v) => !v)}
              aria-pressed={panelOpen}
              aria-expanded={panelOpen}
              aria-haspopup="dialog"
              aria-label={panelOpen ? "Κλείσιμο πίνακα προσβασιμότητας" : "Άνοιγμα πίνακα προσβασιμότητας"}
              className="a11y-open-btn flex flex-col items-center text-custom-letters hover:text-purple-300 transition mb-1 focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full"
            >
              <PersonStanding className={`h-10 w-10 rounded-full border-2 border-custom-letters ${
                panelOpen
                  ? "bg-custom-grad1 text-white border-custom-grad1"
                  : "bg-custom-background"
              }`} />
            </button>
            {panelOpen && (
              <AccessibilityPanel language={language} className="top-0 left-full ml-2" />
            )}
          </div>

          <button
            onClick={toggleLanguage}
            aria-label={language === 'el' ? 'Αλλαγή γλώσσας, τρέχουσα: Ελληνικά' : 'Change language, current: English'}
            className="hover:scale-110 transition flex flex-col items-center gap-1 focus:outline-none focus:ring-2 focus:ring-custom-letters rounded-full"
          >
            <div className="w-10 h-10 flex items-center justify-center bg-custom-background rounded-full border-2 border-custom-letters font-bold text-xs text-custom-letters">
              {language.toUpperCase()}
            </div>
          </button>
        </div>
      </div>
    </>
  );
};

export default ProfileSidebar;
